import React, { FC, useEffect, useState } from 'react'
import { fetchAllAreas, selectAreaById } from '@store/slices/area.slice'
import {
  Button,
  Card,
  Collapse,
  FormControl,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap'
import {
  BsEyeFill,
  BsEyeSlashFill,
  BsPauseFill,
  BsPlayFill,
  BsPlusCircleFill,
  BsX,
} from 'react-icons/bs'
import {
  fetchBroadcastById,
  selectBroadcastById,
  updateBroadcast,
} from '@store/slices/broadcast.slice'
import SlotComponent from '@components/slot/slot.component'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { AudioModel } from '@store/slices/audio.slice'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import {
  createTimeslot,
  selectTimeslotsByIds,
  TimeslotModel,
} from '@store/slices/timeslot.slice'
import {generateAudioForText, generateHeaderPreview} from '@store/thunk/audio.thunk'
import { useForm, UseFormRegister, UseFormWatch } from 'react-hook-form'
import { debounce } from '../../tools/time.tools'
import { fetchAllStations } from '@store/slices/station.slice'
import { fetchAllVoices } from '@store/slices/voice.slice'
import { fetchAllNewss } from '@store/slices/news.slice'

interface AreaComponentProps {
  areaId: EntityId
  broadcastId: EntityId
}

const AreaComponent: FC<AreaComponentProps> = ({ areaId, broadcastId }) => {
  const dispatch = useDispatch<AppDispatch>()
  const area = useSelector((state: RootState) => selectAreaById(state, areaId))
  const broadcast = useSelector((state: RootState) =>
    selectBroadcastById(state, broadcastId)
  )
  const broadcastSlots: TimeslotModel[] = useSelector((state: RootState) =>
    selectTimeslotsByIds(state, broadcast.slots)
  )

  const SlotList = () => {
    const sortedSlots = broadcastSlots.sort(
      (a, b) => a && a && parseInt(a.position) - parseInt(b.position)
    )
    return (
      <div className="p-0">
        {sortedSlots.map((slot) => {
          return (
            slot && (
              <div key={slot.id}>
                <SlotComponent
                  slotId={slot.id}
                  stationId={area.stationId}
                  numberOfSlots={broadcastSlots.length}
                  areaId={area.id}
                  neighbourSlots={sortedSlots}
                ></SlotComponent>
              </div>
            )
          )
        })}
      </div>
    )
  }

  const onAddSlot = (start: boolean) => {
    dispatch(
      createTimeslot({
        broadcastId: broadcast.id,
        position: start ? '0' : broadcastSlots.length.toString(),
        newsId: null,
      })
    )
      .then(() => dispatch(fetchBroadcastById(broadcastId)))
      .catch((e) => console.error(e))
  }

  const AddNewSlot: FC<{ start?: boolean }> = ({ start = false }) => {
    return (
      <div
        className="d-flex w-100 py-2 align-items-center justify-content-center border-secondary rounded-2 mt-2"
        style={{ border: '1px dashed' }}
      >
        <Button variant={'outline-secondary'} onClick={() => onAddSlot(start)}>
          Přidat slot
        </Button>
      </div>
    )
  }

  return (
    <div style={{ marginBottom: '65px' }}>
      <Row>
        <Row className="p-0 m-0">
          <Headers broadcastId={broadcast.id}></Headers>
        </Row>
        {/*{broadcastSlots.length > 0 && (*/}
        {/*  <Row className="p-0 m-0">*/}
        {/*    <AddNewSlot start={true}></AddNewSlot>*/}
        {/*  </Row>*/}
        {/*)}*/}
        <Row className="p-0 m-0">
          <SlotList></SlotList>
        </Row>
        <Row className="p-0 m-0">
          <AddNewSlot></AddNewSlot>
        </Row>
      </Row>
    </div>
  )
}

const HeaderLine: FC<{
  header: { position: string; text: string }
  index: number
  register: UseFormRegister<any>
  watch: UseFormWatch<any>
  onHeaderDelete: (index: number) => void
}> = ({ header, index, register, watch, onHeaderDelete }) => {
  const fieldName = 'header-' + header.position
  const watchFieldValue = watch(fieldName)
  const defaultValue = watchFieldValue ? watchFieldValue : header.text
  return (
    <InputGroup>
      <InputGroup.Text style={{ height: '33.5px' }}>
        {Number(header.position) + 1}
      </InputGroup.Text>
      <FormControl
        as="textarea"
        {...register(fieldName)}
        style={{ fontSize: '13px', height: '25px' }}
        defaultValue={defaultValue}
      ></FormControl>
      <Button
        size={'sm'}
        variant={'danger'}
        onClick={() => onHeaderDelete(index)}
      >
        <BsX></BsX>
      </Button>
    </InputGroup>
  )
}

const Headers: FC<{ broadcastId: EntityId }> = ({ broadcastId }) => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    reset,
    formState: { dirtyFields },
  } = useForm()
  const broadcast = useSelector((state: RootState) =>
    selectBroadcastById(state, broadcastId)
  )
  const [isAudioPlaying, setIsAudioPlaying] = useState<boolean>(false)
  const [isAudioDownloading, setIsAudioDownloading] = useState<boolean>(false)
  const headers: [{ position: string; text: string }] = JSON.parse(
    broadcast.headers
  )
  const [open, setOpen] = useState(true)
  const isDirty = Object.keys(dirtyFields).length > 0

  const onAddHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    dispatch(
      updateBroadcast({
        id: broadcast.id,
        data: {
          ...broadcast,
          headers: JSON.stringify([
            ...headers,
            { position: headers.length.toString(), text: '' },
          ]),
        },
      })
    )
  }

  const playTmpAudio = (tmpAudio: AudioModel) => {
    let audio: HTMLAudioElement | null = document.createElement('audio')

    const cleanup = () => {
      if (audio) {
        audio.removeEventListener('ended', cleanup)
        audio.removeEventListener('canplaythrough', playAudio)
        audio = null
        setIsAudioPlaying(false)
      }
    }

    const playAudio = () => {
      if (!audio) return // safety check
      audio
        .play()
        .then(() => {
          setIsAudioPlaying(true)
        })
        .catch((err: Error) => {
          console.log('Failed to play audio:', err)
          cleanup()
        })
    }

    audio.addEventListener('canplaythrough', playAudio, { once: true }) // play audio when can play through
    audio.addEventListener('ended', cleanup, { once: true }) // clean up when audio ended

    audio.src = tmpAudio.publicPath // this starts loading the audio data
  }

  const downloadAndPlayAudio = () => {
    const newHeaders = headers.map((header) => ({
      ...header,
      text: getValues(`header-${header.position}`),
    }))
    dispatch(
      generateAudioForText({
        voice: broadcast.voiceId,
        text: JSON.stringify(newHeaders),
      })
    )
      .then(unwrapResult)
      .then((audio) => playTmpAudio(audio))
  }

  const downloadAndPlayHeaderPreview = () => {
    if (isAudioDownloading || isAudioPlaying) return
    const newHeaders = headers.map((header) => ({
      ...header,
      text: getValues(`header-${header.position}`),
    }))
    setIsAudioDownloading(true)
    dispatch(
        generateHeaderPreview({
          broadcastId: broadcast.id,
          text: JSON.stringify(newHeaders),
        })
    )
        .then(unwrapResult)
        .then((audio) => playTmpAudio(audio))
        .finally(() => setIsAudioDownloading(false))
  }

  const onHeaderAudioButton = debounce(downloadAndPlayHeaderPreview)

  const onHeaderSave = (data: any) => {
    const newHeaders = headers.map((header) => ({
      ...header,
      text: data[`header-${header.position}`],
    }))
    setIsAudioDownloading(true)
    dispatch(
      updateBroadcast({
        id: broadcast.id,
        data: {
          ...broadcast,
          headers: JSON.stringify(newHeaders),
        },
      })
    )
      .then(unwrapResult)
      .catch((e) => console.error(e))
      .finally(() => setIsAudioDownloading(false))
  }

  const onHeaderDelete = (index: number) => {
    const newHeaders = headers.filter(
      (header) => Number(header.position) !== index
    )
    newHeaders.forEach((item, index) => {
      item.position = index.toString()
    })
    dispatch(
      updateBroadcast({
        id: broadcast.id,
        data: {
          ...broadcast,
          headers: JSON.stringify(newHeaders),
        },
      })
    )
  }

  const onHeaderReset = (e: React.MouseEvent) => {
    e.preventDefault()
    reset()
  }

  return (
    <Card className="bg-light-subtle p-2">
      <div className="d-flex align-items-center gap-1 mb-2">
        <Button
          onClick={onHeaderAudioButton}
          variant={'success'}
          className="px-2 d-flex align-items-center"
        >
          {!isAudioDownloading && isAudioPlaying && (
            <BsPauseFill className=" d-flex align-items-center"></BsPauseFill>
          )}
          {!isAudioDownloading && !isAudioPlaying && (
            <BsPlayFill className=" d-flex align-items-center"></BsPlayFill>
          )}
          {isAudioDownloading && (
            <div style={{ width: '16px', height: '16px'}}>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              <span className="sr-only"></span>
            </div>
          )}
        </Button>
        <Button
          onClick={(e) => onHeaderReset(e)}
          variant={'warning'}
          size={'sm'}
          className="px-2 fw-bold"
          disabled={!isDirty}
        >
          Reset
        </Button>
        <Button
          onClick={handleSubmit(onHeaderSave)}
          variant={'primary'}
          size={'sm'}
          className="px-2 fw-bold"
          disabled={!isDirty}
        >
          Uložit
        </Button>
        <span className={'mx-auto'}>
          <strong>Headlines</strong>
        </span>

        <Button
          onClick={() => setOpen(!open)}
          aria-controls="collapse-text"
          aria-expanded={open}
          variant={'outline-secondary'}
          className="ms-auto"
          size={'sm'}
        >
          {open ? <BsEyeSlashFill /> : <BsEyeFill />}
        </Button>
      </div>
      <Collapse in={open}>
        <form>
          <div>
            {headers.length > 0 &&
              headers.map((header, index) => (
                <HeaderLine
                  header={header}
                  index={index}
                  key={broadcastId.toString() + index.toString()}
                  register={register}
                  watch={watch}
                  onHeaderDelete={onHeaderDelete}
                ></HeaderLine>
              ))}
          </div>
          <Button
            size="sm"
            className="mt d-flex align-items-center justify-content-center"
            variant={'secondary'}
            onClick={onAddHeaderClick}
          >
            <BsPlusCircleFill className="me-2"></BsPlusCircleFill> Přidat header
          </Button>
        </form>
      </Collapse>
    </Card>
  )
}

export default AreaComponent
